import React, { useEffect, useState, useCallback } from "react";
import { Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserForm } from "@/components/forms/ClientUserForms";
import { DeleteUserButton } from "@/components/DeleteUserButton";
import { TableSkeleton } from "@/components/Skeleton";
import { ROLE_LABELS } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function SettingsPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
      setUsers(u.data); setClients((c.data || []).filter((x) => !x.is_archived));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Users &amp; Settings</h2>
          <p className="text-muted-foreground">Manage who can sign in and what they can access</p>
        </div>
        <UserForm clients={clients} onSaved={load} />
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? <TableSkeleton rows={5} cols={6} /> :
            users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-muted-foreground"><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Role</th><th className="px-4 py-3 font-medium">Organization</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium"></th></tr></thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{u.full_name || "—"}</td>
                        <td className="px-4 py-3">{u.email}</td>
                        <td className="px-4 py-3">
                          {ROLE_LABELS[u.role]}
                          {u.role === "client_user" && u.client_role === "super_admin" && (
                            <Badge className="ml-2 bg-indigo-100 text-indigo-800 hover:bg-indigo-100" data-testid={`settings-super-admin-${u.email}`}>
                              Super Admin
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">{u.client_name || "—"}</td>
                        <td className="px-4 py-3">{u.is_active ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <UserForm clients={clients} user={u} onSaved={load} />
                            {me?.id !== u.id && <DeleteUserButton user={u} onDeleted={load} />}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
                <Users className="h-5 w-5" />
                <p>No users yet.</p>
              </div>
            )
          }
        </CardContent>
      </Card>
      <p className="text-sm text-muted-foreground">New accounts created here are active immediately. Users that are inactive cannot sign in until you activate them.</p>
    </div>
  );
}
